import React from 'react';
import {cn} from '@/utils';
import usePriceSelection from './usePriceSelection';

const ID = 'quantity-table';
const PriceList = () => {
  const {
    isFetching,
    prices,
    config,
    priceSequence,
    priceListStyle,
    contactLink,
    dpoData,
    is_custom_shape,
    visibleStart,
    visibleEnd,
    showAll,
    loadingIndex,
    showRangeOverflowText,
    containerRef,
    customQuantityField,
    selectQuantity,
    toggleExpanded,
    onCustomQuantityChange,
    onCustomQuantityKeyDown,
    onCustomQuantityBlur,
  } = usePriceSelection();

  const showMiddleColumn = !!priceListStyle && priceListStyle !== 'qty-price-only';

  const {values, selection_quantity} = prices;
  const visibleValues = showAll ? values : values.slice(visibleStart, visibleEnd);
  const hasMoreItems = !showAll && visibleEnd < values.length;

  const handleKeyDown = (e, listRef) => {
    const items = listRef?.querySelectorAll('[role="option"]');
    if (!items?.length) return;

    const currentIndex = Array.from(items).indexOf(document.activeElement);
    let nextIndex;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
      items[nextIndex].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
      items[nextIndex].focus();
    }
  };

  return (
    <If condition={!is_custom_shape || (is_custom_shape && dpoData)}>
      <CustomQuantityInput
        config={config}
        prices={prices}
        isFetching={isFetching}
        loadingIndex={loadingIndex}
        showRangeOverflowText={showRangeOverflowText}
        contactLink={contactLink}
        customQuantityField={customQuantityField}
        onChange={onCustomQuantityChange}
        onKeyDown={onCustomQuantityKeyDown}
        onBlur={onCustomQuantityBlur}
      />
      <div
        id={ID}
        ref={containerRef}
        className={cn(
          'relative flex w-full flex-col rounded border border-neutral-darker bg-white mb-2xl',
          isFetching && 'opacity-40 cursor-wait'
        )}
        data-quantity={selection_quantity}
        aria-busy={isFetching}
      >
        <TableHeader priceListStyle={priceListStyle} showMiddleColumn={showMiddleColumn} />

        <div className="h-px w-full bg-neutral-darker" role="none" />

        <ul
          id={`listbox-${ID}`}
          role="listbox"
          aria-label={Drupal.t('Select quantity', {}, {context: 'Configurator price list table'})}
          className="m-0 list-none p-0 overflow-hidden transition-[max-height] duration-300 ease-in-out"
          onKeyDown={(e) => handleKeyDown(e, e.currentTarget)}
        >
          {visibleValues.map((item) => (
            <QuantityRow
              key={item.id}
              item={item}
              index={values.indexOf(item)}
              prices={prices}
              priceSequence={priceSequence}
              priceListStyle={priceListStyle}
              showMiddleColumn={showMiddleColumn}
              loadingIndex={loadingIndex}
              onSelect={selectQuantity}
            />
          ))}
        </ul>

        <If condition={hasMoreItems || showAll}>
          <button
            type="button"
            onClick={toggleExpanded}
            aria-expanded={showAll}
            aria-controls={`listbox-${ID}`}
            className="flex items-center justify-center gap-4xs w-full pb-sm pt-2xs bg-transparent border-0 border-t border-solid border-t-neutral-darker cursor-pointer"
          >
            <span className="text-xs font-medium">
              {showAll
                ? Drupal.t('Show less', {}, {context: 'Configurator price list table'})
                : Drupal.t('Show more', {}, {context: 'Configurator price list table'})}
            </span>
            <ft-icon
              name="caret-down"
              height="14"
              aria-hidden="true"
              class={showAll ? 'transition-all rotate-180' : 'transition-all'}
            ></ft-icon>
          </button>
        </If>

        <If condition={isFetching}>
          <div
            className="absolute inset-0 pointer-events-none cursor-wait flex justify-center items-center"
            role="status"
          >
            <ft-spinner size="large" aria-hidden="true"></ft-spinner>
            <span className="sr-only">{Drupal.t('Loading prices', {}, {context: 'Configurator price list table'})}</span>
          </div>
        </If>
      </div>
    </If>
  );
};

const CustomQuantityInput = ({
  config,
  prices,
  isFetching,
  loadingIndex,
  showRangeOverflowText,
  contactLink,
  customQuantityField,
  onChange,
  onKeyDown,
  onBlur,
}) => {
  const customQty = config.custom_quantity;

  return (
    <If condition={customQty?.is_enabled === true}>
      <div className="flex flex-wrap items-center gap-x-sm gap-y-2xs mb-sm tablet:flex-nowrap">
        <span className="shrink-0 text-sm font-semibold whitespace-nowrap desktop:text-base" aria-hidden="true">
          {Drupal.t('Custom quantity :', {}, {context: 'Configurator price list table'})}
        </span>
        <div className="shrink min-w-0">
          <input
            id="custom-qty"
            name="custom-qty"
            aria-label={Drupal.t('Custom quantity', {}, {context: 'Configurator price list table'})}
            aria-describedby={cn('custom-qty-descr', showRangeOverflowText && 'custom-qty-error')}
            type="number"
            className="relative block h-[38px] rounded border border-neutral-darker px-sm text-sm outline-hidden"
            style={{width: `${(Drupal.t('Quantity') + ' ' + prices.labels.plural_price_label).length + 4}ch`}}
            placeholder={
              !isFetching || (isFetching && loadingIndex !== null)
                ? Drupal.t('Quantity') + ' ' + prices.labels.plural_price_label
                : ''
            }
            ref={customQuantityField}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            step={customQty.step}
            min={customQty.min}
            max={customQty.max}
            aria-invalid={showRangeOverflowText || undefined}
            required
          />
        </div>
        <div id="custom-qty-descr" className="flex shrink-0 flex-col">
          <span className="text-2xs font-medium">Min {customQty.min}</span>
          <span className="text-2xs font-medium">Max {customQty.max.toLocaleString()}</span>
        </div>
      </div>
      <If condition={showRangeOverflowText}>
        <div id="custom-qty-error" className="flex items-center gap-xs pb-sm" role="alert">
          <ft-icon name="info" aria-hidden="true"></ft-icon>
          <div className="text-sm">
            {Drupal.t('The quantity must be less than @max pieces.', {'@max': customQty.max.toLocaleString()})}
          </div>
        </div>
      </If>
    </If>
  );
};

const middleColumnLabel = (priceListStyle) => {
  if (priceListStyle === 'savings') return Drupal.t('Saving', {}, {context: 'Configurator price list table'});
  return Drupal.t('Unit price', {}, {context: 'Configurator price list table'});
};

const TableHeader = ({priceListStyle, showMiddleColumn}) => (
  <div className="flex w-full items-center px-lg py-2xs" role="presentation" aria-hidden="true">
    <span className={cn('flex-1 text-xs font-medium', showMiddleColumn ? 'basis-1/2 tablet:basis-2/5' : 'basis-1/2')}>{Drupal.t('Quantity', {}, {context: 'Configurator price list table'})}</span>
    <If condition={showMiddleColumn}>
      <span className="hidden flex-1 basis-1/5 text-center text-xs font-medium tablet:block">{middleColumnLabel(priceListStyle)}</span>
    </If>
    <span className={cn('flex-1 text-right text-xs font-medium', showMiddleColumn ? 'basis-1/2 tablet:basis-2/5' : 'basis-1/2')}>{Drupal.t('Total', {}, {context: 'Configurator price list table'})}</span>
  </div>
);

const QuantityRow = ({item, index, prices, priceSequence, priceListStyle, showMiddleColumn, loadingIndex, onSelect}) => {
  const {quantity, number_of_single_products, price, is_selected, id, discount_percentage} = item;
  const isActive = is_selected || loadingIndex === `${prices.id}_${id}`;
  const primaryPrice = price[priceSequence[0]];
  const secondaryPrice = priceSequence.length > 1 ? price[priceSequence[1]] : null;

  const quantityLabel = `${quantity.toLocaleString()} ${
    quantity === 1 ? prices.labels.singular_price_label : prices.labels.plural_price_label
  }`;

  return (
    <li
      id={`listbox-quantity-${index}`}
      role="option"
      aria-selected={is_selected}
      data-quantity={quantity}
      className={cn(
        'flex w-full items-center px-lg py-2xs text-left transition-colors duration-150',
        'focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-1',
        isActive
          ? 'ring-2 ring-inset ring-brand-1 bg-neutral-light'
          : 'bg-white cursor-pointer hover:bg-neutral-light',
        'border-b border-b-neutral-darker last:border-b-0'
      )}
      tabIndex={is_selected ? 0 : -1}
      onClick={() => onSelect({parentId: prices.id, id, is_selected})}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect({parentId: prices.id, id, is_selected});
        }
      }}
      aria-label={buildOptionLabel({quantityLabel, number_of_single_products, prices, discount_percentage, primaryPrice, secondaryPrice, priceSequence})}
    >
      {/* Quantity */}
      <div className={cn('flex flex-1 items-center gap-2xs', showMiddleColumn ? 'basis-1/2 tablet:basis-2/5' : 'basis-1/2')}>
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2xs">
            <span className="text-sm font-bold" aria-hidden="true">
              {quantityLabel}
            </span>
            <If condition={discount_percentage}>
              <span
                className="inline-flex items-center rounded-full bg-[color:var(--theme-color-secondary)] px-xs py-4xs text-xs font-bold text-white whitespace-nowrap"
                aria-hidden="true"
              >
                -{discount_percentage}%
              </span>
            </If>
          </div>
          <If condition={number_of_single_products > 0}>
            <span className="text-2xs font-medium" aria-hidden="true">
              {number_of_single_products.toLocaleString()}{' '}
              {number_of_single_products === 1
                ? prices.labels.singular_product_label
                : prices.labels.plural_product_label}
            </span>
          </If>
        </div>
      </div>

      {/* Middle column: Unit price or Saving */}
      <If condition={showMiddleColumn}>
        <div className={cn(
          'hidden flex-1 basis-1/5 text-center text-sm tablet:block',
          priceListStyle === 'savings' ? 'font-bold text-[color:var(--theme-color-secondary)]' : 'font-medium',
          discount_percentage ? 'text-[color:var(--theme-color-secondary)]' : ''
        )} aria-hidden="true">
          <Choose>
            <When condition={priceListStyle !== 'savings'}>
              {primaryPrice.formatted_price}
            </When>
            <When condition={!primaryPrice.formatted_special_price_total || !discount_percentage}>
              {primaryPrice.info_text}
            </When>
          </Choose>
        </div>
      </If>

      {/* Total */}
      <div className={cn('flex flex-1 flex-col items-end gap-px', showMiddleColumn ? 'basis-1/2 tablet:basis-2/5' : 'basis-1/2')} aria-hidden="true">
        <PriceTotal
          primaryPrice={primaryPrice}
          secondaryPrice={secondaryPrice}
          priceSequence={priceSequence}
        />
      </div>
    </li>
  );
};

const buildOptionLabel = ({quantityLabel, number_of_single_products, prices, discount_percentage, primaryPrice, secondaryPrice, priceSequence}) => {
  const parts = [quantityLabel];

  if (number_of_single_products > 0) {
    parts.push(
      `${number_of_single_products.toLocaleString()} ${
        number_of_single_products === 1
          ? prices.labels.singular_product_label
          : prices.labels.plural_product_label
      }`
    );
  }

  if (discount_percentage) {
    parts.push(`${discount_percentage}% ${Drupal.t('discount', {}, {context: 'Configurator price list table'})}`);
  }

  const unitPrice = primaryPrice.formatted_special_price || primaryPrice.formatted_price;
  parts.push(`${unitPrice} / ${prices.labels.singular_price_label}`);

  const hasSpecialPrice = !!primaryPrice.formatted_special_price_total;
  if (hasSpecialPrice) {
    parts.push(
      `${Drupal.t('Total', {}, {context: 'Configurator price list table'})}: ${primaryPrice.formatted_special_price_total}, ${Drupal.t('instead of', {}, {context: 'Configurator price list table'})} ${primaryPrice.formatted_price_total}`
    );
  } else {
    parts.push(`${Drupal.t('Total', {}, {context: 'Configurator price list table'})}: ${primaryPrice.formatted_price_total}`);
  }

  if (secondaryPrice) {
    const vatLabel = priceSequence[1] === 'net' ? Drupal.t('excl. VAT') : Drupal.t('incl. VAT');
    const secondaryTotal = hasSpecialPrice
      ? secondaryPrice.formatted_special_price_total
      : secondaryPrice.formatted_price_total;
    parts.push(`${secondaryTotal} ${vatLabel}`);
  }

  return parts.join(', ');
};

const PriceTotal = ({primaryPrice, secondaryPrice, priceSequence}) => {
  const hasSpecialPrice = !!primaryPrice.formatted_special_price_total;

  return (
    <>
      {hasSpecialPrice ? (
        <div className="flex flex-col items-end tablet:flex-row tablet:items-center tablet:gap-4xs">
          <span className="text-2xs font-medium line-through tablet:text-xs">
            {primaryPrice.formatted_price_total}
          </span>
          <span className="text-base font-bold text-[color:var(--theme-color-secondary)] tablet:text-lg">
            {primaryPrice.formatted_special_price_total}
          </span>
        </div>
      ) : (
        <span className="text-lg font-bold text-brand-1">
          {primaryPrice.formatted_price_total}
        </span>
      )}

      <If condition={secondaryPrice}>
        <span className="text-2xs font-medium">
          {hasSpecialPrice
            ? secondaryPrice.formatted_special_price_total
            : secondaryPrice.formatted_price_total}{' '}
          {priceSequence[1] === 'net' ? Drupal.t('excl. VAT') : Drupal.t('incl. VAT')}
        </span>
      </If>
    </>
  );
};

export default PriceList;
